import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { listReconciliations, decideReconciliation } from '../api/financeApi';
import useFinanceQuery from '../useFinanceQuery';
import FinanceListPage from '../components/FinanceListPage';
import DetailLinkButton from '../components/DetailLinkButton';
import { useFinance } from '../financeContext';
import { money } from '../format';
import { StatusBadge } from './rowBits';
import ConfirmModal from '../components/ConfirmModal';
import MessageModal from '../components/MessageModal';

const STATUS_OPTIONS = [
  { value: 'pending_review', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'adjusted', label: 'Adjusted' },
  { value: 'rejected', label: 'Rejected' }
];

export default function BillingAdjustmentsPage() {
  const { canApproveFinance } = useFinance();
  const q = useFinanceQuery(
    (params) => listReconciliations({ status: 'pending_review', ...params }),
    { filterKeys: ['status', 'vertical', 'minAmount', 'maxAmount', 'dateFrom', 'dateTo'] }
  );
  const [note, setNote] = useState({});
  const [busyId, setBusyId] = useState('');
  const [actionError, setActionError] = useState('');
  const [confirmId, setConfirmId] = useState('');

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await decideReconciliation(id, { action, reviewerNotes: note[id] || '' });
      await q.reload();
    } catch (err) {
      setActionError(err?.response?.data?.msg || `Unable to ${action} this adjustment.`);
    } finally {
      setBusyId('');
    }
  };

  const columns = [
    { key: 'ref', header: 'Booking', cardHeader: true, cell: (r) => <DetailLinkButton kind="booking" entity={r} label={`#${r.bookingRef || (r.bookingId || '').slice(-6)}`} /> },
    { key: 'vertical', header: 'Vertical', cell: (r) => r.vertical },
    { key: 'change', header: 'Change', cell: (r) => (
      <span>
        {money(r.before?.totalAmount)} → {money(r.after?.totalAmount)}
        <div className={`small ${r.deltaTotal < 0 ? 'text-danger' : 'text-success'}`}>
          {r.deltaTotal < 0 ? '' : '+'}{money(r.deltaTotal)} · {r.direction}
        </div>
      </span>
    ) },
    { key: 'reason', header: 'Reason', cell: (r) => <span className="small text-muted">"{r.reason}" — {r.requestedBy}</span> },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> }
  ];

  const rowActions = !canApproveFinance ? undefined : (r) => {
    if (r.status !== 'pending_review') return <span className="small text-muted">{r.reviewerNotes || '—'}</span>;
    return (
      <div style={{ minWidth: 180 }}>
        <Form.Control size="sm" className="mb-1" placeholder="Reviewer notes"
          value={note[r._id] || ''} onChange={(e) => setNote((p) => ({ ...p, [r._id]: e.target.value }))} />
        <div className="d-flex gap-1">
          <Button size="sm" variant="success" disabled={busyId === r._id} onClick={() => decide(r._id, 'approve')}>Approve</Button>
          <Button size="sm" variant="outline-danger" disabled={busyId === r._id} onClick={() => setConfirmId(r._id)}>Reject</Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <FinanceListPage
        title="Billing adjustments"
        subtitle="Post-booking billing changes whose revenue-reducing total needs finance sign-off."
        q={q}
        columns={columns}
        rowActions={rowActions}
        filterFields={['status', 'vertical', 'amount', 'date']}
        statusOptions={STATUS_OPTIONS}
        searchPlaceholder="Search booking ref or reason"
        emptyText="Nothing here."
      />
      <ConfirmModal
        show={!!confirmId}
        onHide={() => setConfirmId('')}
        title="Reject this billing adjustment?"
        message="The booking amounts will stay as they are. This cannot be undone from here."
        confirmLabel="Reject"
        confirmVariant="danger"
        busy={busyId === confirmId}
        onConfirm={async () => {
          await decide(confirmId, 'reject');
          setConfirmId('');
        }}
      />
      <MessageModal
        show={!!actionError}
        onHide={() => setActionError('')}
        title="Could not update adjustment"
        message={actionError}
      />
    </>
  );
}
